import type {
    PartnerAuthService,
    PartnerLoginRequest,
    PartnerRegisterRequest,
    PartnerSessionValidateResponse
} from '../../../infrastructure/services/PartnerAuthService';
import type {IUserRepository} from '../../../domain/repositories/IUserRepository';
import {User} from '../../../domain/entities/User';

export interface PartnerAuthenticationResult {
    user: User;
    authTokens: {
        accessToken: string;
        refreshToken: string;
    };
    partner?: string;
    campaignId?: string;
    registrationStatus: string | null;
}

function resolveRegistrationStatus(authResult: {
    registration_status?: string | null;
    user?: { registration_status?: string | null } | null;
}): string | null {
    return authResult.registration_status ?? authResult.user?.registration_status ?? null;
}

export class PartnerAuthenticationUseCase {
    constructor(
        private readonly partnerAuthService: PartnerAuthService,
        private readonly userRepository: IUserRepository
    ) {
    }

    async validatePartnerSession(sessionToken: string): Promise<PartnerSessionValidateResponse> {
        return await this.partnerAuthService.validatePartnerSession(sessionToken);
    }

    async registerPartnerUser(request: PartnerRegisterRequest): Promise<PartnerAuthenticationResult> {
        const authResult = await this.partnerAuthService.registerPartnerUser(request);

        // Create or update user in our database
        let user = await this.userRepository.findByEmail(authResult.user.username);

        if (!user) {
            // Create new user from partner registration
            user = await this.userRepository.create({
                authId: "",
                company: "",
                dateOfBirth: new Date(),
                digitalAddress: "",
                gender: "",
                maritalStatus: "",
                occupation: "",
                salary: "",
                id: authResult.user.id,
                name: `${authResult.user.first_name} ${authResult.user.last_name}`,
                email: authResult.user.email,
                phoneNumber: authResult.user.username,
            });
        } else {
            // Update existing user with partner information
            // user = await this.userRepository.update(user.id, {
            // });
        }

        return {
            user,
            authTokens: {
                accessToken: authResult.access_token,
                refreshToken: authResult.refresh_token
            },
            partner: authResult.partner,
            campaignId: authResult.campaign_id,
            registrationStatus: resolveRegistrationStatus(authResult)
        };
    }

    async loginPartnerUser(request: PartnerLoginRequest): Promise<PartnerAuthenticationResult> {
        const authResult = await this.partnerAuthService.loginPartnerUser(request);

        // Find existing user
        // const user = await this.userRepository.findByPhone(authResult.user.username);
        // if (!user) {
        //     throw new Error('User profile not found');
        // }
        //
        // // Update last login information
        // await this.userRepository.update(user.id, {
        //     updatedAt: new Date()
        // });

        return {
            user: authResult.user as any,
            authTokens: {
                accessToken: authResult.access_token,
                refreshToken: authResult.refresh_token
            },
            partner: authResult.partner,
            campaignId: authResult.campaign_id,
            registrationStatus: resolveRegistrationStatus(authResult)
        };
    }

    parsePartnerToken(url: string): string | null {
        return this.partnerAuthService.parsePartnerToken(url);
    }

    generateDeviceInfo(): any {
        return this.partnerAuthService.generateDeviceInfo();
    }

    generateIdempotencyKey(): string {
        return this.partnerAuthService.generateIdempotencyKey();
    }
}
